<template>
  <div v-if="loading" class="trezo-card bg-white p-[20px] md:p-[25px] rounded-md">
    <div class="flex justify-center items-center p-20">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 opacity-75"></div>
    </div>
  </div>

  <div v-else-if="error" class="trezo-card bg-white p-[20px] md:p-[25px] rounded-md">
    <div class="text-center p-10">
      <div class="text-danger-500 text-3xl mb-3">
        <i class="ri-error-warning-line"></i>
      </div>
      <h3 class="text-xl font-medium mb-2 text-danger-500">Error Loading Property</h3>
      <p class="text-gray-500 dark:text-gray-400">{{ error }}</p>
      <button 
        @click="$router.push('/real-estate/property-list')" 
        class="inline-block mt-4 font-medium px-5 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-400 transition-colors"
      >
        Back to Property List
      </button>
    </div>
  </div>

  <div v-else-if="!property" class="trezo-card bg-white p-[20px] md:p-[25px] rounded-md">
    <div class="text-center p-10">
      <div class="text-gray-400 text-3xl mb-3">
        <i class="ri-building-line"></i>
      </div>
      <h3 class="text-xl font-medium mb-2 text-gray-700 dark:text-gray-300">Property Not Found</h3>
      <p class="text-gray-500 dark:text-gray-400">The property you're looking for doesn't exist or has been removed.</p>
      <button 
        @click="$router.push('/real-estate/property-list')" 
        class="inline-block mt-4 font-medium px-5 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-400 transition-colors"
      >
        Back to Property List
      </button>
    </div>
  </div>

  <div v-else class="trezo-card bg-white p-[20px] md:p-[25px] rounded-md overflow-hidden">
    <div class="trezo-card-content">
      <!-- Property Title -->
      <div class="w-[calc(100%+40px)] md:w-[calc(100%+50px)] bg-primary-500 shadow-md p-4 mb-1 -mx-[20px] md:-mx-[25px] mt-[-20px] md:mt-[-25px]">
        <h2 v-if="property.title" class="text-xl font-medium text-center text-white">
          {{ formatTitle(property.title) }}
        </h2>
      </div>
      
      <!-- Image Gallery -->
      <div class="relative rounded-md">
        <!-- Main Image with Navigation Arrows -->
        <div class="relative overflow-hidden rounded-md">
          <img
            :src="currentImage || placeholderImage"
            class="inline-block rounded-md w-full h-auto object-cover cursor-pointer"
            alt="property-details-image"
            @error="handleImageError"
            @click="openLightbox(currentImageIndex)"
          />
          
          <!-- Navigation Arrows (only shown if there are multiple images) -->
          <template v-if="allImages.length > 1">
            <button 
              @click.prevent="prevImage" 
              class="absolute left-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-r cursor-pointer hover:bg-opacity-70 transition-all z-10"
              aria-label="Previous image"
            >
              <i class="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button 
              @click.prevent="nextImage" 
              class="absolute right-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-l cursor-pointer hover:bg-opacity-70 transition-all z-10"
              aria-label="Next image"
            >
              <i class="ri-arrow-right-s-line text-2xl"></i>
            </button>
          </template>
          
          <!-- Property Status Tag -->
          <span
            class="absolute top-[20px] ltr:right-[20px] rtl:left-[20px] inline-block text-xs rounded-[4px] py-px px-[10px]"
            :class="property.rent_or_sale === 'rent' ? 'bg-secondary-100 text-secondary-600' : 'bg-success-100 text-success-600'"
          >
            {{ property.rent_or_sale === 'rent' ? 'Rent' : 'Sale' }}
          </span>
          
          <!-- Image Counter -->
          <div v-if="allImages.length > 1" class="absolute bottom-[10px] right-[10px] bg-black bg-opacity-60 text-white text-xs py-1 px-2 rounded">
            {{ currentImageIndex + 1 }}/{{ allImages.length }}
          </div>
        </div>
        
        <!-- Thumbnail Gallery -->
        <div v-if="galleryImages.length > 0" class="flex mt-2 overflow-x-auto pb-2 space-x-2">
          <div 
            v-for="(image, index) in allImages" 
            :key="index"
            class="flex-shrink-0 w-[80px] h-[60px] rounded-md overflow-hidden cursor-pointer transition-all"
            :class="{ 'ring-2 ring-primary-500': currentImageIndex === index }"
            @click="setCurrentImage(index)"
          >
            <img 
              :src="image" 
              class="w-full h-full object-cover"
              :alt="`Property image ${index + 1}`"
              @error="handleThumbnailError($event)"
            />
          </div>
        </div>
      </div>
      
      <!-- Lightbox -->
      <div 
        v-if="lightboxOpen" 
        class="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center transition-opacity"
        @click.self="closeLightbox"
      >
        <div class="relative w-full max-w-6xl p-4">
          <!-- Close Button -->
          <button 
            @click="closeLightbox" 
            class="absolute top-2 right-2 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 z-10"
            aria-label="Close lightbox"
          >
            <i class="ri-close-line"></i>
          </button>
          
          <!-- Lightbox Navigation -->
          <button 
            v-if="allImages.length > 1"
            @click.prevent="prevLightboxImage" 
            class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full cursor-pointer hover:bg-opacity-70 transition-all z-10"
            aria-label="Previous image"
          >
            <i class="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button 
            v-if="allImages.length > 1"
            @click.prevent="nextLightboxImage" 
            class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full cursor-pointer hover:bg-opacity-70 transition-all z-10"
            aria-label="Next image"
          >
            <i class="ri-arrow-right-s-line text-2xl"></i>
          </button>
          
          <!-- Lightbox Image -->
          <img 
            :src="allImages[lightboxIndex]" 
            class="max-h-[80vh] max-w-full mx-auto object-contain"
            :alt="`Property image ${lightboxIndex + 1}`"
            @error="handleImageError"
          />
          
          <!-- Image Counter -->
          <div v-if="allImages.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white py-1 px-3 rounded-full text-sm">
            {{ lightboxIndex + 1 }}/{{ allImages.length }}
          </div>
        </div>
      </div>
      
      <h3 class="text-lg mb-[9px] mt-[20px] text-primary-500">
        {{ formattedPrice }} 
      </h3>
      <span class="block relative pt-px ltr:pl-[22px] rtl:pr-[22px]">
        <i
          class="material-symbols-outlined text-primary-500 absolute ltr:-left-[2px] rtl:-right-[2px] top-1/2 -translate-y-1/2 !text-[19px]"
        >
          location_on
        </i>
        {{ property.address }}
      </span>
      <div
        class="mt-[17px] sm:flex items-center justify-between py-[10px] border-y border-primary-50 dark:border-[#172036]"
      >
        <ul class="mb-[10px] sm:mb-0">
          <li
            class="relative inline-block relative ltr:pl-[24px] rtl:pr-[24px] ltr:mr-[20px] rtl:ml-[20px] ltr:last:mr-0 rtl:last:ml-0"
          >
            <i
              class="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 text-primary-500 !text-[18px]"
            >
              bed
            </i>
            {{ property.bedroom || '0' }}Bed
          </li>
          <li
            class="relative inline-block relative ltr:pl-[24px] rtl:pr-[24px] ltr:mr-[20px] rtl:ml-[20px] ltr:last:mr-0 rtl:last:ml-0"
          >
            <i
              class="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 text-primary-500 !text-[18px]"
            >
              bathtub
            </i>
            {{ property.bathroom || '0' }}Bath
          </li>
          <li
            class="relative inline-block relative ltr:pl-[24px] rtl:pr-[24px] ltr:mr-[20px] rtl:ml-[20px] ltr:last:mr-0 rtl:last:ml-0"
          >
            <i
              class="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 text-primary-500 !text-[18px]"
            >
              square_foot
            </i>
            {{ property.size || '0' }}sqft
          </li>
        </ul>
        <div v-if="property.property_type" class="inline-block text-xs rounded-[4px] py-1 px-3 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300">
          {{ property.property_type }}
        </div>
      </div>
      <h3
        class="text-md md:text-lg mt-[20px] md:mt-[25px] lg:mt-[30px] mb-[12px]"
      >
        Description
      </h3>
      <p>{{ property.description || 'No description available.' }}</p>
      
      <template v-if="features.length > 0">
        <h3 class="text-md md:text-lg mt-[20px] md:mt-[25px] lg:mt-[30px] mb-[12px]">
          Features
        </h3>
        <ul>
          <li 
            v-for="(feature, index) in features" 
            :key="index"
            class="relative ltr:pl-[17px] rtl:pr-[17px] mb-[10px] last:mb-0"
          >
            <span
              class="absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full bg-primary-500 mt-px"
            ></span>
            {{ feature }}
          </li>
        </ul>
      </template>
      
      <template v-if="neighborhoodInfo.length > 0">
        <h3 class="text-md md:text-lg mt-[20px] md:mt-[25px] lg:mt-[30px] mb-[12px]">
          Neighborhood
        </h3>
        <ul>
          <li 
            v-for="(info, index) in neighborhoodInfo" 
            :key="index"
            class="relative ltr:pl-[17px] rtl:pr-[17px] mb-[10px] last:mb-0"
          >
            <span
              class="absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full bg-primary-500 mt-px"
            ></span>
            {{ info }}
          </li>
        </ul>
      </template>
      
      <h3 v-if="property.country || property.city" class="text-md md:text-lg mt-[20px] md:mt-[25px] lg:mt-[30px] mb-[12px]">
        Location
      </h3>
      <p v-if="property.country || property.city">
        {{ [property.city, property.country].filter(Boolean).join(', ') }}
      </p>
      
      <template v-if="renovations.length > 0">
        <h3 class="text-md md:text-lg mt-[20px] md:mt-[25px] lg:mt-[30px] mb-[12px]">
          Recent Renovations
        </h3>
        <ul>
          <li 
            v-for="(renovation, index) in renovations" 
            :key="index"
            class="relative ltr:pl-[17px] rtl:pr-[17px] mb-[10px] last:mb-0"
          >
            <span
              class="absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full bg-primary-500 mt-px"
            ></span>
            {{ renovation }}
          </li>
        </ul>
      </template>
      
      <!-- Additional Details -->
      <div v-if="hasAdditionalDetails" class="mt-6">
        <h3 class="text-md md:text-lg mb-[12px]">Additional Details</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div v-if="property.year_built" class="flex items-center">
            <span class="font-medium mr-2">Year Built:</span>
            <span>{{ property.year_built }}</span>
          </div>
          <div v-if="property.availability_date" class="flex items-center">
            <span class="font-medium mr-2">Available From:</span>
            <span>{{ formatDate(property.availability_date) }}</span>
          </div>
          <div v-if="property.floor !== null" class="flex items-center">
            <span class="font-medium mr-2">Floor:</span>
            <span>{{ property.floor }}</span>
          </div>
          <div v-if="property.total_floors !== null" class="flex items-center">
            <span class="font-medium mr-2">Total Floors:</span>
            <span>{{ property.total_floors }}</span>
          </div>
          <div v-if="property.elevator !== null" class="flex items-center">
            <span class="font-medium mr-2">Elevator:</span>
            <span>{{ property.elevator ? 'Yes' : 'No' }}</span>
          </div>
          <div v-if="property.parking" class="flex items-center">
            <span class="font-medium mr-2">Parking:</span>
            <span>{{ property.parking }}</span>
          </div>
          <div v-if="property.furnished !== null" class="flex items-center">
            <span class="font-medium mr-2">Furnished:</span>
            <span>{{ property.furnished ? 'Yes' : 'No' }}</span>
          </div>
          <div v-if="property.pets_allowed !== null" class="flex items-center">
            <span class="font-medium mr-2">Pets Allowed:</span>
            <span>{{ property.pets_allowed ? 'Yes' : 'No' }}</span>
          </div>
          <div v-if="property.entry_type" class="flex items-center">
            <span class="font-medium mr-2">Entry Type:</span>
            <span>{{ property.entry_type }}</span>
          </div>
          <div v-if="property.utilities_included" class="flex items-center">
            <span class="font-medium mr-2">Utilities Included:</span>
            <span>{{ property.utilities_included }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { supabase } from "@/utils/supabase";
import placeholderImage from '@/assets/images/properties/property-placeholder.jpg';
import { formatCurrency, getUserCurrency } from "@/utils/currency";

interface Property {
  id: string;
  org_id: string;
  status: string;
  price: number;
  rent_or_sale: string;
  description: string;
  features: string;
  neighborhood_info: string;
  country: string;
  city: string;
  address: string;
  recent_renovations: string;
  bedroom: number;
  bathroom: number;
  size: number;
  year_built: number;
  availability_date: string;
  floor: number;
  total_floors: number;
  elevator: boolean;
  parking: string;
  furnished: boolean;
  entry_type: string;
  utilities_included: string;
  pets_allowed: boolean;
  main_image_url: string;
  property_type: string;
  created_at: string;
  assigned_to: string;
  created_by: string;
  title: string;
}

interface GalleryImage {
  id: string;
  property_id: string;
  imagelink: string;
  created_at: string;
}

export default defineComponent({
  name: "PropertyDetails",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const property = ref<Property | null>(null);
    const galleryImages = ref<GalleryImage[]>([]);
    const loading = ref(true);
    const error = ref('');
    const userLocale = ref(navigator.language || 'en-US');
    const currencyCode = ref('ILS');
    
    // Gallery state
    const currentImageIndex = ref(0);
    const lightboxOpen = ref(false);
    const lightboxIndex = ref(0);
    
    // Gallery computed properties
    const allImages = computed(() => {
      const images = [];
      
      // Add main image if it exists
      if (property.value?.main_image_url) {
        images.push(property.value.main_image_url);
      }
      
      // Add gallery images
      galleryImages.value.forEach(img => {
        if (img.imagelink) {
          images.push(img.imagelink);
        }
      });
      
      // If no images at all, add placeholder
      if (images.length === 0) {
        images.push(placeholderImage);
      }
      
      return images;
    });
    
    const currentImage = computed(() => {
      return allImages.value[currentImageIndex.value] || placeholderImage;
    });
    
    // Gallery methods
    const setCurrentImage = (index: number) => {
      currentImageIndex.value = index;
    };
    
    const nextImage = () => {
      if (allImages.value.length <= 1) return;
      currentImageIndex.value = (currentImageIndex.value + 1) % allImages.value.length;
    };
    
    const prevImage = () => {
      if (allImages.value.length <= 1) return;
      currentImageIndex.value = (currentImageIndex.value - 1 + allImages.value.length) % allImages.value.length;
    };
    
    const openLightbox = (index: number) => {
      lightboxIndex.value = index;
      lightboxOpen.value = true;
      // Disable body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    };
    
    const closeLightbox = () => {
      lightboxOpen.value = false;
      // Re-enable body scroll
      document.body.style.overflow = '';
    };
    
    const nextLightboxImage = () => {
      if (allImages.value.length <= 1) return;
      lightboxIndex.value = (lightboxIndex.value + 1) % allImages.value.length;
    };
    
    const prevLightboxImage = () => {
      if (allImages.value.length <= 1) return;
      lightboxIndex.value = (lightboxIndex.value - 1 + allImages.value.length) % allImages.value.length;
    };
    
    const handleThumbnailError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      target.src = placeholderImage;
    };
    
    // Handle keydown events for lightbox navigation
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!lightboxOpen.value) return;
      
      switch(event.key) {
        case 'ArrowRight':
          nextLightboxImage();
          break;
        case 'ArrowLeft':
          prevLightboxImage();
          break;
        case 'Escape':
          closeLightbox();
          break;
      }
    };
    
    // קבלת המטבע של הארגון
    const getCurrency = async () => {
      try {
        currencyCode.value = await getUserCurrency();
      } catch (error) {
        console.error('Error fetching currency:', error);
      }
    };
    
    onMounted(async () => {
      document.addEventListener('keydown', handleKeyDown);
      await getCurrency();
      loadProperty();
    });
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeyDown);
      
      // Make sure to reset body overflow in case component is unmounted while lightbox is open
      if (lightboxOpen.value) {
        document.body.style.overflow = '';
      }
    });

    const features = computed(() => {
      if (!property.value?.features) return [];
      return property.value.features.split(',').map(feature => feature.trim()).filter(Boolean);
    });

    const neighborhoodInfo = computed(() => {
      if (!property.value?.neighborhood_info) return [];
      return property.value.neighborhood_info.split(',').map(info => info.trim()).filter(Boolean);
    });

    const renovations = computed(() => {
      if (!property.value?.recent_renovations) return [];
      return property.value.recent_renovations.split(',').map(renovation => renovation.trim()).filter(Boolean);
    });

    const hasAdditionalDetails = computed(() => {
      if (!property.value) return false;
      
      return property.value.year_built ||
        property.value.availability_date ||
        property.value.floor !== null ||
        property.value.total_floors !== null ||
        property.value.elevator !== null ||
        property.value.parking ||
        property.value.furnished !== null ||
        property.value.pets_allowed !== null ||
        property.value.entry_type ||
        property.value.utilities_included;
    });

    const formatPrice = (price: number | null) => {
      if (!price) return formatCurrency(0, currencyCode.value);
      return formatCurrency(price, currencyCode.value);
    };

    const formattedPrice = computed(() => {
      if (!property.value?.price) return formatCurrency(0, currencyCode.value);
      return formatCurrency(property.value.price, currencyCode.value);
    });

    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }).format(date);
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      target.src = placeholderImage;
    };

    const loadProperty = async () => {
      try {
        loading.value = true;
        error.value = '';
        
        const propertyId = route.query.id;
        if (!propertyId) {
          throw new Error('Property ID is required');
        }

        // Fetch property details
        const { data, error: fetchError } = await supabase
          .from('property')
          .select('*')
          .eq('id', propertyId)
          .single();

        if (fetchError) {
          throw new Error(fetchError.message);
        }

        if (!data) {
          property.value = null;
          return;
        }

        property.value = data as Property;
        
        // Fetch gallery images
        await loadGalleryImages(propertyId as string);
      } catch (err) {
        console.error('Error loading property:', err);
        error.value = err instanceof Error ? err.message : 'An unknown error occurred';
      } finally {
        loading.value = false;
      }
    };
    
    const loadGalleryImages = async (propertyId: string) => {
      try {
        const { data, error: galleryError } = await supabase
          .from('property_gallery')
          .select('*')
          .eq('property_id', propertyId)
          .order('created_at', { ascending: true });
          
        if (galleryError) {
          console.error('Error loading gallery images:', galleryError);
          return;
        }
        
        galleryImages.value = data as GalleryImage[];
      } catch (err) {
        console.error('Error in loadGalleryImages:', err);
      }
    };

    const formatTitle = (title: string) => {
      // Check if title appears to be in English (simplified approach)
      const isEnglish = /^[A-Za-z0-9\s.,!?'"()-]+$/.test(title);
      
      if (isEnglish) {
        // Title case - capitalize first letter of each word
        return title
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      // Return original title for non-English text
      return title;
    };

    return {
      property,
      loading,
      error,
      features,
      neighborhoodInfo,
      renovations,
      hasAdditionalDetails,
      formatPrice,
      formattedPrice,
      formatDate,
      handleImageError,
      formatTitle,
      
      // Gallery related properties and methods
      galleryImages,
      currentImageIndex,
      currentImage,
      allImages,
      setCurrentImage,
      nextImage,
      prevImage,
      lightboxOpen,
      lightboxIndex,
      openLightbox,
      closeLightbox,
      nextLightboxImage,
      prevLightboxImage,
      handleThumbnailError
    };
  }
});
</script>
